/**
 * View-instance identity + per-instance state.
 *
 * A panel renders ONLY its active tab, so switching tabs unmounts the view.
 * Anything a page keeps in component state dies with it — which is why opening
 * a second Options Chain tab used to blank the first one. These two pieces fix
 * that:
 *
 *   useViewInstance()          — which workspace instance am I rendering as?
 *   useViewState(key, initial) — useState, but stored on that instance
 *
 * State written through `useViewState` lives on the ViewInstance in the
 * workspace tree, so it survives unmount AND a page reload, and two instances
 * of the same view never see each other's values.
 *
 * Deliberately NOT solved by keeping hidden tabs mounted: a hidden Options
 * Chain would keep polling, and concurrent chains already share IB's ~100
 * streaming-line allowance (see MAX_TOTAL_SUBS in brokers/ib_direct/chain.py).
 *
 * Outside a panel — the pop-out window, a direct route, tests — there is no
 * instance, and `useViewState` degrades to plain `useState`.
 */
import {
  createContext, useCallback, useContext, useState,
  type Dispatch, type SetStateAction,
} from 'react'

import { useWorkspaceStore } from '@/stores/workspaceStore'
import { findViewState, TRANSIENT_PREFIX } from './tree'

export interface ViewInstanceInfo {
  instanceId: string
  panelId: string
  viewId: string
  /** Route params this instance was opened with (e.g. `{ symbol: 'TSLA' }`). */
  params?: Record<string, string>
}

/** Provided by PanelContainer around the mounted view. */
export const ViewInstanceContext = createContext<ViewInstanceInfo | null>(null)

/** The workspace instance this view renders as, or null when outside a panel. */
export function useViewInstance(): ViewInstanceInfo | null {
  return useContext(ViewInstanceContext)
}

/**
 * `useState` whose value is scoped to — and persisted with — this view instance.
 *
 * `initial` is evaluated once per mount and used only when the instance has no
 * stored value for `key`. That makes it the natural place to read a global
 * user default (`localStorage`), which then seeds each NEW tab without the
 * tabs sharing a value afterwards.
 */
export function useViewState<T>(
  key: string,
  initial: T | (() => T),
  opts?: {
    /**
     * Keep across tab switches but drop on reload. For work-in-progress that
     * shouldn't reappear days later — a half-built order, a staged roll.
     */
    transient?: boolean
  },
): [T, Dispatch<SetStateAction<T>>] {
  const instance = useContext(ViewInstanceContext)
  const instanceId = instance?.instanceId ?? null
  const slot = opts?.transient ? TRANSIENT_PREFIX + key : key

  // Boxed so the initializer runs exactly once and the seed never changes
  // identity, whichever path below we take.
  const [seed] = useState<{ v: T }>(() => ({
    v: typeof initial === 'function' ? (initial as () => T)() : initial,
  }))

  // Fallback path — no panel context, so behave exactly like useState.
  const [local, setLocal] = useState<T>(seed.v)

  // Same object reference between writes, so unrelated store updates (focus,
  // split sizes) don't re-render the view.
  const bag = useWorkspaceStore((s) => (
    instanceId ? findViewState(s.rootNode, instanceId) : undefined
  ))
  const write = useWorkspaceStore((s) => s.setViewState)

  const has = !!bag && Object.prototype.hasOwnProperty.call(bag, slot)
  const value = instanceId ? (has ? (bag![slot] as T) : seed.v) : local

  const set = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    if (!instanceId) { setLocal(next); return }
    write(instanceId, slot, next, seed.v)
  }, [instanceId, slot, write, seed])

  return [value, set]
}
